import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import type { AnalysisRebuildStatus } from '../types';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { extractErrorMessage } from '../utils/extractErrorMessage';
import Button from './ui/Button';
import ActionRow from './ui/ActionRow';
import { Checkbox } from './pilot-ui/checkbox';
import { RadioGroup, RadioGroupItem } from './pilot-ui/radio-group';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './pilot-ui/dialog';

const STANDARD_KEY = 'standard_daily_v1';

export interface ClipAnalysisMethods {
  methods: {
    analysis_key: string;
    display_name: string;
    description: string;
    comparison_only: boolean;
    enabled: boolean;
    data_kept: boolean;
    days_processed: number;
    latest_processed_day: string | null;
    rebuild: AnalysisRebuildStatus;
  }[];
  default: string | null;
  switches: Record<string, string>;
  period: { start: string | null; source: string };
  revision: string;
  implicit_default: boolean;
}

export interface ClipAnalysisSaveResult {
  methods: ClipAnalysisMethods['methods'];
  removed: string[];
  no_complete_days: boolean;
  rebuilds: { analysis_key: string; queued_dates: string[]; failed_dates: string[] }[];
}

const sameKeys = (a: string[], b: string[]) => [...a].sort().join('\n') === [...b].sort().join('\n');
const shownDefault = (state: ClipAnalysisMethods) =>
  state.switches.daily_filter_health_metrics ?? state.default ?? '';
const splitConfiguration = (state: ClipAnalysisMethods, selected: string) =>
  state.default !== selected || Object.values(state.switches).some(key => key !== selected);

function analysisSaveMessage(result: ClipAnalysisSaveResult, methods: ClipAnalysisMethods['methods']) {
  const name = (key: string) => methods.find(m => m.analysis_key === key)?.display_name ?? key;
  const deleted = result.removed.filter(key => key !== STANDARD_KEY);
  const messages = deleted.map(key => `Deleted ${name(key)} results.`);
  if (result.removed.includes(STANDARD_KEY)) messages.push(`Turned off ${name(STANDARD_KEY)}; data kept.`);
  for (const rebuild of result.rebuilds) {
    if (rebuild.queued_dates.length) messages.push(`Queued ${rebuild.queued_dates.length} ${rebuild.queued_dates.length === 1 ? 'day' : 'days'} for ${name(rebuild.analysis_key)}.`);
  }
  const failed = result.rebuilds.reduce((count, rebuild) => count + rebuild.failed_dates.length, 0);
  if (failed) messages.push(`The change is stored; ${failed} ${failed === 1 ? 'day' : 'days'} failed to queue and will be recovered in the background.`);
  if (result.no_complete_days) messages.push('The change is stored. No complete days are available to queue in the current filter period.');
  return messages.join(' ') || 'Analysis methods saved.';
}

interface Props {
  deviceId: number;
  onClose: () => void;
  onSaved: (message: string, result: ClipAnalysisSaveResult) => void;
}

export default function ClipAnalysisMethodsDialog({ deviceId, onClose, onSaved }: Props) {
  const [stored, setStored] = useState<ClipAnalysisMethods | null>(null);
  const [methods, setMethods] = useState<string[]>([]);
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(false);
  const [loadVersion, setLoadVersion] = useState(0);
  const url = `${config.devicesApiUrl}/analysis-methods/devices/${deviceId}`;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setStored(null);
    setError('');
    void axiosInstance.get<ClipAnalysisMethods>(url).then(({ data }) => {
      if (!active) return;
      setStored(data);
      setMethods(data.methods.filter(m => m.enabled).map(m => m.analysis_key));
      setSelected(shownDefault(data));
      setReload(false);
    }).catch((err: unknown) => {
      if (active) setError(extractErrorMessage(err, 'Could not load this Clip’s analysis methods.'));
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [url, loadVersion]);

  const changed = stored && (!sameKeys(methods, stored.methods.filter(m => m.enabled).map(m => m.analysis_key))
    || selected !== shownDefault(stored) || splitConfiguration(stored, selected));
  const reason = saving ? 'Saving analysis methods…' : loading ? 'Loading analysis methods…'
    : !stored ? 'Load the stored methods before saving.' : !selected ? 'No default is set. Choose one.'
      : !changed ? 'No changes to save.' : null;
  const close = () => { if (!savingRef.current) onClose(); };

  const save = async () => {
    if (savingRef.current || !stored || reason) return;
    savingRef.current = true;
    setSaving(true);
    setError('');
    try {
      const { data } = await axiosInstance.put<ClipAnalysisSaveResult>(url, {
        methods, default: selected, revision: stored.revision,
      });
      onSaved(analysisSaveMessage(data, stored.methods), data);
    } catch (err: unknown) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (status === 409) {
        setError(extractErrorMessage(err,
          "This Clip's methods changed since you opened this. Reload to see them.", { verbatimMessage: true }));
        setReload(true);
      } else if (status && status >= 400 && status < 500) {
        setError(extractErrorMessage(err));
      } else {
        setError('The save may or may not have been stored. Reload to see the stored methods.');
        setReload(true);
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };
  const name = (key: string) => stored?.methods.find(m => m.analysis_key === key)?.display_name ?? key;
  return (
    <Dialog open onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent closeDisabled={saving} className="sm:max-w-2xl flex max-h-[90dvh] flex-col">
        <DialogHeader>
          <DialogTitle>Analysis methods for this Clip</DialogTitle>
          <DialogDescription>Tick the methods to run each night. Choose which method drives filter life. Save applies all changes.</DialogDescription>
        </DialogHeader>
        {error && <div role="alert">{error}</div>}
        {(reload || (!loading && !stored)) && <Button type="button" variant="secondary" title="Reload this Clip's stored analysis methods" disabled={saving} onClick={() => setLoadVersion(v => v + 1)}>Reload</Button>}
        {loading && <p role="status">Loading analysis methods…</p>}
        {stored && <>
          <div className="min-h-0 overflow-y-auto space-y-4">
            <p>Current filter period starts {stored.period.start ?? 'when retained readings are available'} ({stored.period.source}).</p>
            {!selected && <p>No default is set. Choose one.</p>}
            {selected && splitConfiguration(stored, selected) && <p role="status">
              {stored.switches.daily_filter_health_metrics ? `Filter life comes from ${name(stored.switches.daily_filter_health_metrics)}` : 'Filter life is off'},
              but the default flag is {stored.default ? name(stored.default) : 'not set'}.
              {Object.entries(stored.switches).filter(([, owner]) => owner !== selected).map(([key, owner]) =>
                ` ${key.replace(/_/g, ' ')} comes from ${name(owner)}.`)}
              {' '}Save gives everything to the method you choose.
            </p>}
            <RadioGroup aria-label="Default analysis method" value={selected} onValueChange={value => { if (typeof value === 'string' && !savingRef.current) setSelected(value); }}>
              {stored.methods.map(method => {
                const key = method.analysis_key;
                const ticked = methods.includes(key);
                const isDefault = selected === key;
                const unticked = method.enabled && !ticked;
                const helpId = `method-${key}-help`;
                return <div key={key} className="border rounded-md p-3 space-y-2">
                  <h3 className="font-medium">{method.display_name}</h3>
                  <p id={`method-${key}-description`} className="text-sm text-muted-foreground">{method.description}</p>
                  <div className="flex items-center gap-2">
                    <Checkbox nativeButton render={<Button variant="secondary" className="p-0" title={`Turn ${method.display_name} on or off for this Clip`} />}
                      id={`method-${key}-enabled`} aria-label={`Run ${method.display_name}`} checked={ticked} disabled={saving || isDefault}
                      aria-describedby={`${helpId} method-${key}-description`} onCheckedChange={checked => {
                        if (savingRef.current || isDefault) return;
                        setMethods(prev => checked ? [...prev, key] : prev.filter(k => k !== key));
                      }} />
                    <label htmlFor={`method-${key}-enabled`}>Run each night</label>
                    <RadioGroupItem value={key} aria-label={`Default: ${method.display_name}`} disabled={saving || !ticked || method.comparison_only} aria-describedby={helpId} />
                    <span>Default</span>
                  </div>
                  <div id={helpId} className="text-xs text-muted-foreground">
                    {isDefault && <p>Choose another default before removing this method.</p>}
                    {method.comparison_only && <p>Comparison only: cannot drive filter life.</p>}
                    {((method.data_kept && !ticked) || (unticked && key === STANDARD_KEY)) && <p>
                      {key === STANDARD_KEY ? "Turned off; data kept. The standard method's data is never deleted." : 'Turned off; data kept.'}
                    </p>}
                    {unticked && key !== STANDARD_KEY && <p>Saving deletes this method's daily results for this Clip.</p>}
                    {!ticked && !unticked && !method.data_kept && <p>Tick to build this method’s current filter period.</p>}
                  </div>
                  <p className="text-xs">{method.days_processed} days processed in this period; latest: {method.latest_processed_day ?? 'none'}.</p>
                </div>;
              })}
            </RadioGroup>
          </div>
        </>}
        <ActionRow align="end" reason={reason} reasonId="clip-methods-save-reason">
          <Button type="button" variant="secondary" title="Discard these changes and close" disabled={saving} onClick={close}>Cancel</Button>
          <Button type="button" variant="primary" title="Save this Clip's analysis methods and queue the required days" disabled={!!reason} aria-describedby={reason ? 'clip-methods-save-reason' : undefined} onClick={() => void save()}>{saving ? 'Saving…' : 'Save'}</Button>
        </ActionRow>
      </DialogContent>
    </Dialog>
  );
}
